import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ElementType, PixelCoordinate } from '../types';

interface CurrentPhoto {
  id?: string; // Photo ID from database
  projectId?: string; // Project ID for loading anomalies
  file: File;
  dataUrl: string;
  width: number;
  height: number;
}

export type AnnotationMode = 'point' | 'rectangle' | 'polygon' | null;

interface UIStore {
  // Current view
  currentView: 'map' | 'photo';

  // Selected element
  selectedElementId: string | number | null;
  selectedElementType: ElementType | null;

  // Current photo
  currentPhoto: CurrentPhoto | null;

  // Annotation mode
  annotationMode: AnnotationMode;
  pendingAnnotationPoints: PixelCoordinate[];

  // Modal states
  isAnomalyFormOpen: boolean;
  pendingAnomalyCoords: { x: number; y: number } | null;
  pendingAnnotationType: 'Point' | 'Rectangle' | 'Polygon';
  
  // Edit mode
  editingAnomalyId: string | null;

  // Fast marking mode settings
  fastMarkingMode: boolean;
  autoAdvanceAfterAnomaly: boolean;

  // Highlighted photo on map
  highlightedPhotoId: string | null;

  // Actions
  setView: (view: 'map' | 'photo') => void;
  selectElement: (id: string | number | null, type: ElementType | null) => void;
  setCurrentPhoto: (photo: CurrentPhoto | null) => void;
  
  // Annotation actions
  setAnnotationMode: (mode: AnnotationMode) => void;
  addAnnotationPoint: (point: PixelCoordinate) => void;
  undoLastPoint: () => void;
  clearPendingAnnotation: () => void;
  finishAnnotation: () => void;
  
  openAnomalyForm: (x: number, y: number) => void;
  openAnomalyFormWithAnnotation: (points: PixelCoordinate[], type: 'Point' | 'Rectangle' | 'Polygon') => void;
  closeAnomalyForm: () => void;
  editAnomaly: (anomalyId: string) => void;
  reset: () => void;
  
  // Fast marking mode actions
  setFastMarkingMode: (enabled: boolean) => void;
  setAutoAdvanceAfterAnomaly: (enabled: boolean) => void;
  
  // Map highlight actions
  highlightPhotoOnMap: (photoId: string | null) => void;
}

export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      currentView: 'map',
      selectedElementId: null,
      selectedElementType: null,
      currentPhoto: null,
      annotationMode: null,
      pendingAnnotationPoints: [],
      isAnomalyFormOpen: false,
      pendingAnomalyCoords: null,
      pendingAnnotationType: 'Point',
      editingAnomalyId: null,
      fastMarkingMode: false,
      autoAdvanceAfterAnomaly: true,
      highlightedPhotoId: null,

      setView: (view) => set({ currentView: view }),

      selectElement: (id, type) => set({ 
        selectedElementId: id, 
        selectedElementType: type 
      }),

      setCurrentPhoto: (photo) => set({ 
        currentPhoto: photo,
        annotationMode: null,
        pendingAnnotationPoints: []
      }),

      setAnnotationMode: (mode) => set({ 
        annotationMode: mode,
        pendingAnnotationPoints: []
      }),

      addAnnotationPoint: (point) => set((state) => ({
        pendingAnnotationPoints: [...state.pendingAnnotationPoints, point]
      })),

      undoLastPoint: () => set((state) => ({
        pendingAnnotationPoints: state.pendingAnnotationPoints.slice(0, -1)
      })),

      clearPendingAnnotation: () => set({
        annotationMode: null,
        pendingAnnotationPoints: []
      }),

      finishAnnotation: () => {
        const { annotationMode, pendingAnnotationPoints } = get();
        if (annotationMode === 'point' && pendingAnnotationPoints.length === 1) {
          // Point mode - open form with single point
          set({
            isAnomalyFormOpen: true,
            pendingAnomalyCoords: pendingAnnotationPoints[0],
            pendingAnnotationType: 'Point',
            annotationMode: null
          });
        } else if (annotationMode === 'polygon' && pendingAnnotationPoints.length >= 3) {
          // Polygon mode - open form with polygon points
          set({
            isAnomalyFormOpen: true,
            pendingAnomalyCoords: pendingAnnotationPoints[0], // Use first point as reference
            pendingAnnotationType: 'Polygon',
            annotationMode: null
          });
        }
      },

      openAnomalyForm: (x, y) => set({ 
        isAnomalyFormOpen: true, 
        pendingAnomalyCoords: { x, y },
        pendingAnnotationType: 'Point',
        pendingAnnotationPoints: [{ x, y }]
      }),

      openAnomalyFormWithAnnotation: (points, type) => set({
        isAnomalyFormOpen: true,
        pendingAnomalyCoords: points[0] || { x: 0, y: 0 },
        pendingAnnotationType: type,
        pendingAnnotationPoints: points,
        annotationMode: null  // Reset mode but keep points
      }),

      closeAnomalyForm: () => set({ 
        isAnomalyFormOpen: false, 
        pendingAnomalyCoords: null,
        pendingAnnotationPoints: [],
        editingAnomalyId: null
      }),

      editAnomaly: (anomalyId) => set({
        isAnomalyFormOpen: true,
        editingAnomalyId: anomalyId
      }),

      reset: () => set({
        currentView: 'map',
        selectedElementId: null,
        selectedElementType: null,
        currentPhoto: null,
        annotationMode: null,
        pendingAnnotationPoints: [],
        isAnomalyFormOpen: false,
        pendingAnomalyCoords: null,
        pendingAnnotationType: 'Point',
        editingAnomalyId: null,
      }),

      // Fast marking mode actions
      setFastMarkingMode: (enabled) => set({ fastMarkingMode: enabled }),
      
      setAutoAdvanceAfterAnomaly: (enabled) => set({ autoAdvanceAfterAnomaly: enabled }),
      
      // Map highlight actions
      highlightPhotoOnMap: (photoId) => set({ highlightedPhotoId: photoId }),
    }),
    {
      name: 'elifly-ui-settings',
      // Only persist user preferences, not transient state
      partialize: (state) => ({
        fastMarkingMode: state.fastMarkingMode,
        autoAdvanceAfterAnomaly: state.autoAdvanceAfterAnomaly,
      }),
    }
  )
);
